(ns web3.components.tabs
  (:require ["hammerjs" :as hammer]
            [reagent.core :as r]
            [web3.view :as ui-util]
            [goog.dom :as gdom]
            [goog.dom.classes :as classes]
            [goog.events :as events]
            [web3.utils :as utils]
            [web3.view :as v]
            [web3.ui-state :as state]
            [web3.icons.shapes :as shapes]))

(defn nav-button
  "Tab scroller navigation (prev/next)"
  [props icon]
  [:div.items-center.flex.text-dark-gray.cursor-pointer.flex-none.overflow-hidden.z-4.relative.flex-none
   (-> props
       (update :style merge {:transition "0.2s width linear"
                             :transition-delay "0.1s"}))
   [:div.flex-auto]
   icon
   [:div.flex-auto]])

(defn element-measurements
  "Return width and leftOffset for element."
  [left-offset element]
  {:width (.-width (.getBoundingClientRect element))
   :left (- (.-offsetLeft element)
            left-offset)})

(defn tab
  "Render a single tab. Pass `:selected? true` for selected tab."
  [{:keys [key label selected? href page] :as props}]
  [(if href :a :div)
   (merge {:key key}
          (-> props
              (update :class str " text-carousel inline-block text-center pad-v-3 pad-h-4 cursor-pointer relative nowrap text-dark-gray")
              (dissoc :label :selected? :left :width :page)
              (cond-> selected? (update :class str " text-black"))))
   label
   (when selected?
     [:span.absolute.bottom-0.bg-blue
      {:style {:height "2px"
               :left "1rem"
               :right "1rem"}}])])

(defn width-seq-operator
  "Implements width-based `take` and `drop` operations for from `tabs`

  Each tab is a map containing a numerical `:width` key."
  ([operation max-width tabs] (width-seq-operator operation max-width tabs nil))
  ([operation max-width tabs {:keys [min]}]
   (loop [remaining-tabs tabs
          tabs-taken []
          page-width 0]
     (let [next-tab (first remaining-tabs)]
       (if (or (empty? remaining-tabs)
               (> (+ page-width (:width next-tab)) max-width))
         (case operation :take (if-not min tabs-taken (or (seq tabs-taken) (take min remaining-tabs)))
                         :drop (if-not min remaining-tabs (if (seq tabs-taken) remaining-tabs (drop min remaining-tabs))))
         (recur (rest remaining-tabs)
                (conj tabs-taken next-tab)
                (+ page-width (:width next-tab))))))))

(def take-width (partial width-seq-operator :take))
(def drop-width (partial width-seq-operator :drop))

(defn split-by-width
  "Returns a sequence of 'pages' of tabs.
   Each 'page' is a list of tabs with a total width <= `max-width`,
   but with a minimum of one tab regardless of width."
  [max-width tabs]
  (loop [remaining-tabs tabs
         out []]
    (if (empty? remaining-tabs)
      out
      (let [next-tabs (take-width max-width remaining-tabs {:min 1})]
        (recur (drop (count next-tabs) remaining-tabs)
               (conj out next-tabs))))))

(defn measure-tabs!
  "Take measurements of the tab-bar's tabs, and write to the component's state."
  [{:keys [state] :as this}]
  (let [root (r/dom-node this)
        root-width (.-offsetWidth root)
        {:keys [indicator-width tabs]
         :or {indicator-width 40}} (r/props this)
        visible-container (gdom/findNode root #(classes/has % "measurements"))
        visible-width (- (.-offsetWidth root) (* 2 indicator-width))
        visible-left (.-offsetLeft visible-container)
        children (-> (js/Array.prototype.slice.call (gdom/getChildren visible-container))
                     (js->clj))]
    ;; only update measurements when container width has changed
    (when (or (:first-load? @state)
              (and (not= visible-width (:visible-width @state))
                   (seq children)))
      (let [tabs (map (fn [tab element]
                        (merge tab (element-measurements visible-left element))) tabs children)
            paged-tabs (mapcat (fn [[page tabs]]
                                 (map #(assoc % :page page) tabs)) (partition 2 (interleave (range) (split-by-width visible-width tabs))))]
        (swap! state merge
               {:first-load? (not (contains? @state :tabs-measured))
                :tabs-measured paged-tabs
                :visible-width visible-width})))))

(defn tabs-calculations [{:keys [tabs-measured
                                 offset-calc
                                 sticky-selected?
                                 selected-tab
                                 visible-width]}]
  (let [last-tab (last tabs-measured)
        end-left (+ (:left last-tab) (:width last-tab))
        offset (-> offset-calc
                   (min (+ end-left
                           (- visible-width)))
                   (cond-> sticky-selected? (-> (min (:left selected-tab))
                                                (max (+ (:left selected-tab)
                                                        (:width selected-tab)
                                                        (- visible-width)))))
                   (cond-> (< end-left visible-width) (max (-> (- visible-width end-left)
                                                               (/ 2)
                                                               (-)))))
        start-tab (utils/find-first #(>= (:left %) offset) tabs-measured)
        after-tabs (drop-while #(not= (:key %) (:key start-tab)) tabs-measured)
        before-tabs (take-while #(not= (:key %) (:key start-tab)) tabs-measured)

        next-page-start-tab (let [next-tab (first (drop-width visible-width after-tabs))]
                              (if (= next-tab start-tab)
                                (second after-tabs)
                                next-tab))
        prev-page-start-tab (or (last (take-width visible-width (reverse before-tabs)))
                                (last before-tabs))]
    {:prev-page-tab prev-page-start-tab
     :next-page-tab next-page-start-tab
     :offset offset}))

(def TAB-HEIGHT 56)

(v/defview tab-bar
  {:get-initial-state
   (fn [this]
     (let [{:keys [tabs selected]} (r/props this)]
       {:first-load? true
        :sticky-selected? true}))
   :component-did-mount
   (fn [^js this]
     ;; re-render on scroll, to set fixed position
     (let [key (events/listen js/window "scroll" #(when (neg? (.-scrollY js/window))
                                                    (classes/remove (js/document.getElementById "tab-bar")
                                                                    #js ["fixed"])) true)]
       (v/on-unmount! this :scroll-listener #(events/unlistenByKey key))))
   :component-did-update
   (fn [this] (measure-tabs! this))
   :component-will-update
   (fn [{:keys [state props]} [_ {selected-tab-key :selected
                                  next-tabs :tabs}]]
     (let [{prev-selected-tab-key :selected
            prev-tabs :tabs} props
           {:keys [visible-width tabs-measured start-tab-key first-load?]} @state]
       (when (or first-load?
                 (not= prev-tabs next-tabs)
                 (not= selected-tab-key prev-selected-tab-key))
         (let [selected-tab (utils/find-first #(= (:key %) selected-tab-key) tabs-measured)
               first-tab (utils/find-first #(= (:page %) (:page selected-tab)) tabs-measured)]
           (ui-util/swap-silently! state assoc
                                   :offset-calc (:left first-tab)
                                   :first-load? (not= prev-tabs next-tabs)
                                   :sticky-selected? true)))))}
  [{:keys [selected
           on-change
           tabs
           props
           when-selected
           fixed?
           indicator-width]
    :or {indicator-width 50}}]

  ;; depend in window width
  @state/ui-globals

  (let [{:keys [state] :as this} (r/current-component)
        {:keys [tabs-measured carousel pan offset-calc]} @state
        scroll-y (.-scrollY js/window)
        fixed? (and fixed? (not (neg? scroll-y)))]

    ;; render invisible tabs for measurement purposes
    [:div
     [:div#tab-bar.tabs-root.bg-white
      {:style {:height TAB-HEIGHT
               :box-shadow "0 0 2px rgba(0,0,0,0.1)"}
       :key "tabs-root"
       :class (when fixed?
                "fixed left-0 right-0 z-3")}
      [:div.measurements.nowrap.overflow-hidden
       {:ref #(measure-tabs! this)
        :style {:visibility "hidden"
                :height 0}}
       (doall (map tab tabs))]

      ;; only render the real tabs after we have measurements
      (when tabs-measured
        (let [{:keys [prev-page-tab
                      offset
                      next-page-tab]} (tabs-calculations (assoc @state
                                                           :selected-tab (utils/find-first #(= (:key %) selected) tabs-measured)))]

          [:div.flex.items-stretch.bg-white.overflow-hidden
           (nav-button {:key "back"
                        :on-click (when prev-page-tab #(swap! state assoc
                                                              :offset-calc (:left prev-page-tab)
                                                              :sticky-selected? false))
                        :style {:width (if prev-page-tab indicator-width 20)
                                :margin-right -20
                                :padding-right 10
                                :background "linear-gradient(90deg, rgba(255,255,255,1) 70%, rgba(255,255,255,0))"}}
                       (when prev-page-tab shapes/chevron-left))

           [:div.flex-auto.relative
            [:div.z-2
             {:key "carousel"
              :ref #(when (and % (not carousel))
                      (v/swap-silently! state assoc :carousel %)
                      (let [H (new hammer %)]
                        (.on H "pan" (fn [^js e]
                                       (let [delta (- (.-deltaX e))]
                                         (if (.-isFinal e)
                                           (let [{:keys [offset]} (tabs-calculations
                                                                   (assoc @state
                                                                     :selected-tab (utils/find-first
                                                                                    (fn [x]
                                                                                      (= (:key x) selected))
                                                                                    tabs-measured)))
                                                 {:keys [tabs-measured
                                                         visible-width]} @state
                                                 start (+ offset delta)]
                                             (let [offset-calc (if (pos? delta)
                                                                 (let [end-offset (-> start
                                                                                      (+ visible-width)
                                                                                      (- (* 2 indicator-width)))
                                                                       last-tab (or (first (filter (fn [{:keys [left]}]
                                                                                                     (> left end-offset))
                                                                                                   tabs-measured))
                                                                                    (last tabs-measured))]
                                                                   (max 0 (- (+ (:left last-tab)
                                                                                (:width last-tab))
                                                                             visible-width)))
                                                                 (first (keep (fn [{:keys [left width]}]
                                                                                (when (> (+ left width)
                                                                                         start)
                                                                                  left))
                                                                              tabs-measured)))]
                                               (swap! state assoc
                                                      :pan nil
                                                      :offset-calc offset-calc
                                                      :sticky-selected? false)))
                                           (swap! state
                                                  assoc :pan (- delta))))))))
              :style {:transform (str "translateX(" (-> (- offset)
                                                        (+ (or pan 0))) "px)")
                      :transition (when-not pan "0.3s all ease-in-out")
                      :width 9999}}
             (doall (for [{:keys [key] :as props} tabs-measured]
                      (tab (assoc props :selected? (= selected key)
                                        :on-click #(when on-change (on-change key))))))]]

           (nav-button {:key "next"
                        :on-click (when next-page-tab #(swap! state assoc
                                                              :offset-calc (:left next-page-tab)
                                                              :sticky-selected? false))
                        :style {:width (if next-page-tab indicator-width 20)
                                :padding-left 10
                                :margin-left -20
                                :background "linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,1) 30%)"}}
                       (when next-page-tab shapes/chevron-right))]))]
     (when fixed?
       [:div {:key "tabs-spacer"
              :style {:height TAB-HEIGHT}}])]))
